import { useEffect, useState } from 'react'
import { Download, Bell, Share, X } from 'lucide-react'
import { usePWAInstall } from '../utils/usePWAInstall'
import { usePushNotifications } from '../utils/usePushNotifications'

const DISMISS_KEY = 'appnudge_dismissed'

function isMobile() {
  try { return window.matchMedia('(max-width: 768px)').matches } catch { return false }
}

function isStandalone() {
  try {
    return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
      || window.navigator.standalone === true
  } catch { return false }
}

const barStyle = {
  position: 'fixed', bottom: 'calc(var(--tab-bar-h, 60px) + .75rem)',
  left: '1rem', right: '1rem', zIndex: 850,
  background: 'var(--card)', border: '1px solid rgba(var(--accent-rgb),.3)',
  borderRadius: 'var(--radius)', padding: '.7rem .85rem',
  display: 'flex', alignItems: 'center', gap: '.65rem',
  boxShadow: '0 4px 20px rgba(0,0,0,.4)',
}

export default function MobileAppNudge() {
  const { canInstall, install } = usePWAInstall()
  const { supported, subscribed, loading, requestAndSubscribe } = usePushNotifications()

  const [mobile, setMobile] = useState(isMobile)
  const [standalone, setStandalone] = useState(isStandalone)
  const [dismissed, setDismissed] = useState(() => {
    try { return !!sessionStorage.getItem(DISMISS_KEY) } catch { return false }
  })
  const [showIosHelp, setShowIosHelp] = useState(false)

  useEffect(() => {
    const onResize = () => { setMobile(isMobile()); setStandalone(isStandalone()) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1') } catch {}
    setDismissed(true)
  }

  if (!mobile || dismissed) return null

  // Decide which state to show (first applicable).
  let content = null

  if (!standalone) {
    // (a) Not installed — prompt to install.
    content = (
      <>
        <div style={{ fontSize: '1.35rem', flexShrink: 0 }}>📲</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '.84rem', lineHeight: 1.35 }}>
            Tətbiqi telefonunuza yükləyin
          </div>
          <div style={{ fontSize: '.72rem', color: 'var(--muted)', lineHeight: 1.4 }}>
            Daha sürətli işləyir və bildiriş alırsınız.
          </div>
        </div>
        {canInstall ? (
          <button
            onClick={install}
            className="btn btn-primary btn-sm"
            style={{ fontSize: '.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            <Download size={14} style={{ marginRight: 4 }} /> Yüklə
          </button>
        ) : (
          <button
            onClick={() => setShowIosHelp(true)}
            className="btn btn-secondary btn-sm"
            style={{ fontSize: '.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Necə?
          </button>
        )}
      </>
    )
  } else if (supported && !subscribed) {
    // (b) Installed but push not enabled.
    content = (
      <>
        <div style={{ fontSize: '1.35rem', flexShrink: 0 }}>🔔</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '.84rem', lineHeight: 1.35 }}>
            Bildirişləri aktivləşdirin
          </div>
          <div style={{ fontSize: '.72rem', color: 'var(--muted)', lineHeight: 1.4 }}>
            Yeni müraciət və mesajlardan xəbərdar olun.
          </div>
        </div>
        <button
          onClick={requestAndSubscribe}
          className="btn btn-primary btn-sm"
          disabled={loading}
          style={{ fontSize: '.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {loading ? <span className="spinner" /> : (<><Bell size={14} style={{ marginRight: 4 }} /> Aktivləşdir</>)}
        </button>
      </>
    )
  } else {
    // (c) Nothing to nudge.
    return null
  }

  return (
    <>
      <div style={barStyle}>
        {content}
        <button
          onClick={dismiss}
          aria-label="Bağla"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--muted)', padding: '.2rem', flexShrink: 0,
            display: 'flex', alignItems: 'center',
          }}
        >
          <X size={16} />
        </button>
      </div>

      {showIosHelp && (
        <div
          onClick={() => setShowIosHelp(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,.78)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{ width: '100%', maxWidth: 420, padding: '1.5rem' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.75rem' }}>
              <Share size={20} style={{ color: 'var(--accent)' }} />
              <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: 'var(--text)' }}>
                Ana ekrana əlavə et
              </h2>
            </div>
            <p style={{ fontSize: '.85rem', color: 'var(--muted)', lineHeight: 1.7, margin: '0 0 1.25rem' }}>
              Safari-də aşağıdakı Paylaş düyməsinə basın → "Ana ekrana əlavə et" seçin.
            </p>
            <button
              onClick={() => setShowIosHelp(false)}
              className="btn btn-primary btn-full"
            >
              Anladım
            </button>
          </div>
        </div>
      )}
    </>
  )
}
